import * as THREE from 'three';
import { cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, limb, ring } from '../prims.ts';
import { gear } from './clockwork.ts';
import { figure } from './heroes.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * Nadd's workshop (docs/sendas/artifice.md, chapter 1): its people and its golems, painted miniatures.
 *
 * - Grillo: Nadd's first golem, small as a big dog, bronze gone green, a barrel of a body on two short bowed legs, long
 *   arms that reach the ground, a domed head with one red lamp for an eye, a stubby chimney; a letter tied at his neck.
 * - Nadd: an old dwarf of the Hondura, blind, broad and short, a white beard in two braids, bald with a fringe, a leather
 *   apron over dark wool, the clan's flask at his neck, his belt of bronze keys, a short hammer for a cane; milky eyes.
 * - Dunna: twenty, quick, a slate jerkin and a red scarf, her hair tied back, a crossbow in her hands, bolts at the hip.
 * - The workshop's golem: taller than a man, an iron barrel chest with a red lamp at its front, heavy arms (the right
 *   a hammer fist), stout legs; in its back the boiler, a riveted drum behind a hinged lid, a chimney.
 * - The Overseer: the biggest of them, two boilers side by side in its back, a foreman's whistle on its shoulder, a
 *   crest of plates, a hammer arm and a grab arm.
 * - Tormo, unfinished (the chapter's close): a golem the size of a tower sitting against the wall, headless, its chest
 *   open on an empty cavity, iron plates with rivets, the shoulders' stone blocks held on with clamps.
 *
 * Colors: cold iron and slate, bronze gone green (never gold), the lamps and the boilers' glow the dragon's red.
 */

const IRON = '#34383e';
const IRON_D = '#22252a';
const IRON_L = '#5a6069';
const BRONZE = '#5a4e3e';
const PATINA = '#5d8a7c';
const LAMP_RED = '#ff4f3a';

/** A rivet row along a line on a parent (small domes). */
const rivets = (parent: THREE.Object3D, mat: THREE.Material, from: THREE.Vector3, to: THREE.Vector3, n: number, r = 0.018): void => {
  const geo = new THREE.SphereGeometry(r, 5, 4);
  for (let i = 0; i < n; i++) {
    const k = n === 1 ? 0.5 : i / (n - 1);
    M(geo, mat, parent).position.lerpVectors(from, to, k);
  }
};

/** Grillo: small, bronze gone green, one red lamp for an eye. */
const grillo = (o?: BaseOpts): Model => {
  const c = begin(o, 0.42, 871);
  const g = c.g;
  // (A little bigger than a dog next to the hero: at the game's distance he has to read.)
  g.scale.setScalar(1.3);
  const bronze = metal(BRONZE, { roughness: 0.55, flatShading: true });
  const patina = std(PATINA, { roughness: 0.8, flatShading: true });
  const dark = metal('#2a2622', { roughness: 0.6 });
  const lamp = emis('#ffd0c4', LAMP_RED, 2.2);
  // The barrel of a body, banded, green in its seams.
  const body = M(new THREE.CylinderGeometry(0.27, 0.3, 0.46, 12), bronze, g);
  body.position.y = 0.52;
  for (const y of [0.34, 0.52, 0.7]) ring(g, 0.302, 0.022, patina, y, 4, 14);
  rivets(g, dark, V(-0.18, 0.62, 0.24), V(0.18, 0.62, 0.24), 5);
  // Two short legs, bowed, big flat feet.
  for (const k of [-1, 1]) {
    limb(g, V(k * 0.14, 0.32, 0), V(k * 0.17, 0.08, 0.03), 0.06, 0.07, dark, 6);
    const foot = M(new THREE.BoxGeometry(0.16, 0.07, 0.24), bronze, g);
    foot.position.set(k * 0.17, 0.035, 0.06);
  }
  // The long arms, down to the ground, hooked hands.
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.07, 8, 6), patina, g).position.set(k * 0.3, 0.66, 0.02);
    limb(g, V(k * 0.31, 0.64, 0.02), V(k * 0.36, 0.36, 0.08), 0.04, 0.045, bronze, 6);
    limb(g, V(k * 0.36, 0.36, 0.08), V(k * 0.33, 0.1, 0.16), 0.035, 0.04, bronze, 6);
    const hook = M(new THREE.TorusGeometry(0.05, 0.014, 4, 8, Math.PI * 1.3), dark, g);
    hook.position.set(k * 0.33, 0.07, 0.19);
    hook.rotation.set(0, Math.PI / 2, 0);
  }
  // The dome of a head, its one lamp.
  const head = G(g, 0, 0.78, 0.02);
  M(new THREE.SphereGeometry(0.2, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), bronze, head).position.y = 0;
  ring(head, 0.2, 0.02, patina, 0.0, 4, 14);
  const rim = M(new THREE.TorusGeometry(0.085, 0.022, 6, 14), dark, head);
  rim.position.set(0, 0.07, 0.17);
  rim.rotation.x = -0.25;
  const eye = M(new THREE.SphereGeometry(0.068, 10, 8), lamp, head);
  eye.position.set(0, 0.07, 0.17);
  glowSprite(head, LAMP_RED, 0.55, V(0, 0.07, 0.24), 0.85);
  // The chimney behind, a little bent.
  const ch = M(new THREE.CylinderGeometry(0.035, 0.045, 0.2, 7), dark, g);
  ch.position.set(0.08, 0.86, -0.18);
  ch.rotation.x = -0.3;
  // The letter tied at his neck: a rolled paper and its string.
  const letter = M(new THREE.CylinderGeometry(0.03, 0.03, 0.18, 8), std('#d6d4ce', { roughness: 0.9 }), g);
  letter.position.set(-0.08, 0.74, 0.25);
  letter.rotation.z = Math.PI / 2 - 0.2;
  ring(g, 0.205, 0.008, std('#7a3028', { roughness: 1 }), 0.76, 3, 12);
  const tick = (n: number): void => {
    (eye.material as THREE.MeshStandardMaterial).emissiveIntensity = 1.9 + 0.4 * Math.sin(n * 1.7);
    head.rotation.y = Math.sin(n * 0.4) * 0.18;
  };
  return done(c, 1.3, tick);
};

/** Nadd: an old blind dwarf, a white beard in two braids, the apron, the flask, the keys. */
const nadd = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 872);
  const wool = cloth('#3a3a42');
  const apron = leather('#3e3129');
  const skin = std('#c49a84', { roughness: 0.88 });
  const f = figure({ legs: cloth('#2e2d33'), boots: leather('#231c18'), torso: wool, shoulders: wool, arms: wool, forearms: cloth('#45444c'), hands: skin, skin });
  c.g.add(f.fig);
  // Short and broad.
  f.fig.scale.set(1.24, 0.8, 1.2);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The apron, scorched at the hem, its strap.
  const ap = M(new THREE.BoxGeometry(0.34, 0.66, 0.02), apron, b);
  ap.position.set(0, 1.05, 0.2);
  ap.rotation.x = -0.06;
  M(new THREE.BoxGeometry(0.34, 0.06, 0.022), std('#1e1915', { roughness: 1 }), b).position.set(0, 0.74, 0.215);
  // The belt of keys: bronze keys hanging on a ring at the left hip.
  const keys = G(b, -0.2, 0.92, 0.12);
  const bronze = metal(BRONZE, { roughness: 0.5 });
  M(new THREE.TorusGeometry(0.05, 0.01, 4, 10), bronze, keys);
  for (let i = 0; i < 4; i++) {
    const key = G(keys, -0.03 + i * 0.022, -0.06, 0);
    M(new THREE.BoxGeometry(0.012, 0.09, 0.006), bronze, key).position.y = -0.03;
    M(new THREE.TorusGeometry(0.014, 0.005, 3, 8), bronze, key).position.y = 0.02;
    key.rotation.z = (i - 1.5) * 0.18;
  }
  // The clan's flask at his neck: a little vial of dark glass on a cord, a red glint inside.
  ring(b, 0.12, 0.007, leather('#2a201a'), 1.46, 3, 10);
  const flask = M(new THREE.CylinderGeometry(0.028, 0.032, 0.07, 8), std('#1a1820', { roughness: 0.2, metalness: 0.3 }), b);
  flask.position.set(0.02, 1.33, 0.205);
  M(new THREE.SphereGeometry(0.016, 6, 5), emis('#5a1810', LAMP_RED, 0.8), b).position.set(0.02, 1.33, 0.225);
  // Bald on top, a white fringe; the beard long, in two braids; heavy brows over milky eyes.
  const white = std('#e6e4df', { roughness: 1 });
  const fringe = M(new THREE.SphereGeometry(0.13, 12, 8, 0, Math.PI * 2, Math.PI * 0.42, Math.PI * 0.2), white, h);
  fringe.position.set(0, 1.62, -0.02);
  const beard = M(new THREE.SphereGeometry(0.12, 10, 8, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55), white, h);
  beard.position.set(0, 1.56, 0.05);
  beard.scale.set(1.15, 1.25, 0.9);
  for (const k of [-1, 1]) {
    for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.03 - i * 0.003, 6, 5), white, h).position.set(k * 0.05, 1.43 - i * 0.055, 0.12 - i * 0.008);
    M(new THREE.SphereGeometry(0.016, 6, 5), std('#c9d0d6', { roughness: 0.3, emissive: '#3a4048' }), h).position.set(k * 0.044, 1.656, 0.113);
    M(new THREE.BoxGeometry(0.058, 0.022, 0.03), white, h).position.set(k * 0.046, 1.688, 0.108);
  }
  const nose = M(new THREE.SphereGeometry(0.035, 8, 6), std('#b9876e', { roughness: 0.8 }), h);
  nose.position.set(0, 1.62, 0.13);
  nose.scale.set(1, 1.1, 1.2);
  // A short hammer for a cane in the right hand, its head down.
  const cane = G(f.rhAbs, 0.3, 0.9, 0.14);
  limb(cane, V(0, -0.82, 0), V(0, 0.08, 0), 0.016, 0.018, leather('#3a2a1e'), 5);
  M(new THREE.BoxGeometry(0.1, 0.06, 0.06), metal(IRON, { roughness: 0.5 }), cane).position.set(0, -0.84, 0);
  f.rig.head.rotation.x = -0.08;
  return done(c, 1.46, () => undefined, f.rig);
};

/** Dunna: twenty, the slate jerkin and a red scarf, her crossbow. */
const dunna = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 873);
  const jerkin = leather('#3f4652');
  const skin = std('#d2a88e', { roughness: 0.85 });
  const f = figure({ legs: cloth('#2d3038'), boots: leather('#221c18'), torso: jerkin, shoulders: cloth('#4a5260'), arms: cloth('#4a5260'), forearms: leather('#3a3e46'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.93, 0.97, 0.93);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The red scarf at her neck, its tail over the shoulder.
  const red = cloth('#9b3a30');
  ring(b, 0.13, 0.04, red, 1.45, 5, 12);
  const tail = M(new THREE.BoxGeometry(0.07, 0.26, 0.02), red, b);
  tail.position.set(0.1, 1.32, -0.14);
  tail.rotation.set(0.25, 0, 0.2);
  // Bolts at the hip, a little quiver; a wrench in the belt (she mends fast).
  const quiver = M(new THREE.CylinderGeometry(0.045, 0.04, 0.26, 8), leather('#2c241e'), b);
  quiver.position.set(0.2, 0.92, -0.06);
  quiver.rotation.z = -0.3;
  for (let i = 0; i < 3; i++) M(new THREE.CylinderGeometry(0.006, 0.006, 0.12, 4), std('#8a8e96'), b).position.set(0.23 + i * 0.012, 1.08, -0.07);
  M(new THREE.BoxGeometry(0.03, 0.16, 0.015), metal(IRON_L, { roughness: 0.4 }), b).position.set(-0.19, 0.9, 0.12);
  // Her hair, dark, tied back in a tail.
  const hair = std('#2b211d', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.133, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h);
  top.position.set(0, 1.652, -0.012);
  top.scale.set(1.04, 1, 1.08);
  const pony = M(new THREE.ConeGeometry(0.04, 0.22, 6), hair, h);
  pony.position.set(0, 1.56, -0.16);
  pony.rotation.x = 0.5;
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.044, 0.01, 0.015), hair, h).position.set(k * 0.044, 1.684, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.017, 0.045, 5), std('#c99c84', { roughness: 0.85 }), h);
  nose.position.set(0, 1.628, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  // The crossbow, held across the body: its stock, its bow, its string.
  const xb = G(f.rhAbs, 0.24, 1.0, 0.24);
  xb.rotation.y = -0.5;
  M(new THREE.BoxGeometry(0.04, 0.05, 0.42), leather('#3a2a1e'), xb);
  const bow = M(new THREE.TorusGeometry(0.2, 0.014, 4, 12, Math.PI * 0.8), metal(IRON_L, { roughness: 0.45 }), xb);
  bow.position.set(0, 0, 0.18);
  bow.rotation.set(Math.PI / 2, 0, Math.PI * 0.1);
  M(new THREE.BoxGeometry(0.34, 0.004, 0.004), std('#c9c6bf'), xb).position.set(0, 0, 0.1);
  f.rig.armR.rotation.x = -0.5;
  f.rig.armL.rotation.x = -0.6;
  return done(c, 1.72, () => undefined, f.rig);
};

/**
 * A workshop golem's body: `big` the Overseer (two boilers, the whistle, the crest). Faces +Z; the boiler in its back
 * (−Z), behind a hinged lid with a hot seam.
 */
const golemBody = (o: BaseOpts | undefined, big: boolean): Model => {
  const c = begin(o, big ? 0.95 : 0.72, big ? 875 : 874);
  const g = c.g;
  const iron = metal(IRON, { roughness: 0.55, flatShading: true });
  const ironD = metal(IRON_D, { roughness: 0.65, flatShading: true });
  const plate = metal(IRON_L, { roughness: 0.42, flatShading: true });
  const bronze = metal(BRONZE, { roughness: 0.5, flatShading: true });
  const patina = std(PATINA, { roughness: 0.85, flatShading: true });
  const lamp = emis('#ffd0c4', LAMP_RED, 2.4);
  const hot = emis('#5a1810', LAMP_RED, 1.6);
  // The chest: an iron barrel, a plate on its front, bands.
  const chest = M(new THREE.CylinderGeometry(0.42, 0.36, 0.78, 10), iron, g);
  chest.position.y = 1.22;
  const front = M(new THREE.BoxGeometry(0.5, 0.56, 0.08), plate, g);
  front.position.set(0, 1.26, 0.36);
  for (const y of [0.9, 1.56]) ring(g, 0.43, 0.03, bronze, y, 4, 14);
  rivets(g, ironD, V(-0.2, 1.52, 0.41), V(0.2, 1.52, 0.41), 5, 0.022);
  rivets(g, ironD, V(-0.2, 1.0, 0.41), V(0.2, 1.0, 0.41), 5, 0.022);
  // The lamp at its front: a round red glass in an iron hood.
  const hood = M(new THREE.CylinderGeometry(0.14, 0.17, 0.12, 12, 1, true, Math.PI * 0.15, Math.PI * 0.7), ironD, g);
  hood.position.set(0, 1.34, 0.43);
  hood.rotation.x = Math.PI / 2;
  const eye = M(new THREE.SphereGeometry(0.11, 12, 8), lamp, g);
  eye.position.set(0, 1.3, 0.42);
  eye.scale.set(1, 1, 0.6);
  glowSprite(g, LAMP_RED, 0.9, V(0, 1.3, 0.52), 0.8);
  // A low head, almost sunk in the shoulders, two slits.
  const head = M(new THREE.BoxGeometry(0.34, 0.22, 0.3), plate, g);
  head.position.set(0, 1.74, 0.06);
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.08, 0.02, 0.02), hot, g).position.set(k * 0.08, 1.76, 0.22);
  // The shoulders and the arms: heavy, down to the knees; the right ends in a hammer fist.
  for (const k of [-1, 1]) {
    const sh = M(new THREE.SphereGeometry(0.2, 10, 7), plate, g);
    sh.position.set(k * 0.52, 1.5, 0);
    sh.scale.set(1.1, 0.85, 1.05);
    limb(g, V(k * 0.56, 1.42, 0), V(k * 0.62, 0.98, 0.1), 0.1, 0.12, iron, 7);
    M(new THREE.SphereGeometry(0.1, 8, 6), patina, g).position.set(k * 0.62, 0.98, 0.1);
    limb(g, V(k * 0.62, 0.98, 0.1), V(k * 0.6, 0.62, 0.22), 0.09, 0.1, iron, 7);
    if (k > 0) {
      const fist = M(new THREE.BoxGeometry(0.3, 0.26, 0.3), ironD, g);
      fist.position.set(k * 0.6, 0.5, 0.26);
      rivets(g, bronze, V(k * 0.48, 0.6, 0.42), V(k * 0.72, 0.6, 0.42), 3, 0.024);
    } else {
      // A grab: three thick fingers.
      for (let i = 0; i < 3; i++) {
        const fing = M(new THREE.CylinderGeometry(0.035, 0.03, 0.2, 6), ironD, g);
        fing.position.set(k * 0.6 + (i - 1) * 0.06, 0.48, 0.28);
        fing.rotation.x = 0.4;
      }
    }
  }
  // Stout legs, wide feet.
  for (const k of [-1, 1]) {
    limb(g, V(k * 0.22, 0.86, 0), V(k * 0.26, 0.2, 0.03), 0.12, 0.14, iron, 7);
    const foot = M(new THREE.BoxGeometry(0.28, 0.14, 0.4), ironD, g);
    foot.position.set(k * 0.26, 0.07, 0.07);
  }
  M(new THREE.CylinderGeometry(0.36, 0.3, 0.16, 10), ironD, g).position.y = 0.86;
  // The boiler(s) in the back, each a riveted drum behind a hinged lid with a hot seam, a chimney over it.
  const boilers = big ? [-0.21, 0.21] : [0];
  for (const x of boilers) {
    const drum = M(new THREE.CylinderGeometry(big ? 0.2 : 0.24, big ? 0.2 : 0.24, 0.62, 12), bronze, g);
    drum.position.set(x, 1.24, -0.42);
    ring(g, big ? 0.205 : 0.245, 0.02, ironD, 0, 3, 12).position.set(x, 1.0, -0.42);
    ring(g, big ? 0.205 : 0.245, 0.02, ironD, 0, 3, 12).position.set(x, 1.48, -0.42);
    // The lid, its hinge at the top, its seam glowing.
    const lid = M(new THREE.BoxGeometry(big ? 0.24 : 0.28, 0.3, 0.04), plate, g);
    lid.position.set(x, 1.22, -0.64);
    M(new THREE.BoxGeometry(big ? 0.26 : 0.3, 0.018, 0.02), hot, g).position.set(x, 1.06, -0.665);
    M(new THREE.CylinderGeometry(0.02, 0.02, big ? 0.24 : 0.28, 6), ironD, g).position.set(x, 1.38, -0.66);
    g.children[g.children.length - 1]!.rotation.z = Math.PI / 2;
    const ch = M(new THREE.CylinderGeometry(0.05, 0.06, 0.34, 8), ironD, g);
    ch.position.set(x, 1.72, -0.48);
  }
  if (big) {
    // The foreman's whistle on its left shoulder, a crest of plates over its back.
    const wh = M(new THREE.CylinderGeometry(0.035, 0.05, 0.22, 8), bronze, g);
    wh.position.set(-0.52, 1.74, -0.06);
    for (let i = 0; i < 4; i++) {
      const pl = M(new THREE.BoxGeometry(0.44 - i * 0.06, 0.05, 0.16), plate, g);
      pl.position.set(0, 1.92 + i * 0.05, -0.12 - i * 0.07);
      pl.rotation.x = -0.4;
    }
  }
  const tick = (n: number): void => {
    (eye.material as THREE.MeshStandardMaterial).emissiveIntensity = 2.1 + 0.35 * Math.sin(n * 2.3);
  };
  return done(c, big ? 2.3 : 1.95, tick);
};

/** Tormo, unfinished: sitting against the wall, headless, its chest open, the shoulders' stones clamped on. */
const tormoHull = (o?: BaseOpts): Model => {
  const c = begin(o, 1.6, 876);
  const g = c.g;
  // As big as a tower next to the people round it.
  g.scale.setScalar(1.75);
  const iron = metal('#2e3238', { roughness: 0.6, flatShading: true });
  const ironD = metal('#1c1f23', { roughness: 0.7, flatShading: true });
  const plate = metal('#4c525a', { roughness: 0.5, flatShading: true });
  const stone = std('#6f747b', { roughness: 0.95, flatShading: true });
  const bronze = metal(BRONZE, { roughness: 0.55 });
  // Seated: the hips low, the thighs forward, the shins down, the feet before it.
  M(new THREE.BoxGeometry(1.6, 0.7, 1.1), iron, g).position.set(0, 0.5, -0.2);
  for (const k of [-1, 1]) {
    limb(g, V(k * 0.5, 0.6, 0), V(k * 0.6, 0.75, 1.3), 0.32, 0.36, iron, 8);
    limb(g, V(k * 0.6, 0.75, 1.3), V(k * 0.62, 0.15, 1.6), 0.26, 0.3, plate, 8);
    M(new THREE.BoxGeometry(0.6, 0.3, 0.8), ironD, g).position.set(k * 0.62, 0.15, 1.85);
  }
  // The trunk, leaning back on the wall: a frame of iron round an open chest.
  const trunk = G(g, 0, 0.9, -0.3);
  trunk.rotation.x = -0.18;
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.32, 2.2, 0.9), iron, trunk).position.set(k * 0.78, 1.1, 0);
  M(new THREE.BoxGeometry(1.9, 0.36, 0.95), plate, trunk).position.set(0, 2.2, 0);
  M(new THREE.BoxGeometry(1.4, 0.3, 0.9), plate, trunk).position.set(0, 0.1, 0);
  // The back plate, and the empty chest: a dark cavity with ribs of iron across it, and nothing in it.
  M(new THREE.BoxGeometry(1.4, 1.9, 0.14), ironD, trunk).position.set(0, 1.1, -0.38);
  for (let i = 0; i < 4; i++) M(new THREE.BoxGeometry(1.36, 0.07, 0.08), iron, trunk).position.set(0, 0.45 + i * 0.45, 0.38);
  rivets(trunk, bronze, V(-0.78, 0.2, 0.47), V(-0.78, 2.1, 0.47), 8, 0.04);
  rivets(trunk, bronze, V(0.78, 0.2, 0.47), V(0.78, 2.1, 0.47), 8, 0.04);
  // The shoulders: stone blocks clamped on with iron.
  for (const k of [-1, 1]) {
    const sh = M(new THREE.BoxGeometry(0.75, 0.6, 0.85), stone, trunk);
    sh.position.set(k * 1.12, 2.32, 0);
    sh.rotation.z = k * 0.12;
    for (const dz of [-0.3, 0.3]) M(new THREE.BoxGeometry(0.8, 0.07, 0.07), ironD, trunk).position.set(k * 1.12, 2.62, dz);
    // The arms resting on the thighs, the hands open and down.
    limb(trunk, V(k * 1.2, 2.1, 0.1), V(k * 1.05, 0.9, 0.9), 0.26, 0.3, iron, 8);
    limb(trunk, V(k * 1.05, 0.9, 0.9), V(k * 0.9, 0.2, 1.6), 0.22, 0.26, plate, 8);
    M(new THREE.BoxGeometry(0.42, 0.18, 0.5), ironD, trunk).position.set(k * 0.88, 0.1, 1.85);
  }
  // No head: the neck's collar empty, a few bolts sticking up.
  const collar = M(new THREE.TorusGeometry(0.34, 0.08, 6, 14), ironD, trunk);
  collar.position.set(0, 2.42, 0);
  collar.rotation.x = Math.PI / 2;
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    M(new THREE.CylinderGeometry(0.03, 0.03, 0.16, 5), bronze, trunk).position.set(Math.cos(a) * 0.34, 2.5, Math.sin(a) * 0.34);
  }
  // A big gear half set in its side (Nadd's work, not done).
  const gg = gear(trunk, 0.32, 14, bronze, 0.06);
  gg.position.set(-0.95, 1.3, 0.3);
  gg.rotation.y = Math.PI / 2;
  return done(c, 5.6, () => undefined);
};

const workGolem = (o?: BaseOpts): Model => golemBody(o, false);
const capataz = (o?: BaseOpts): Model => golemBody(o, true);

/** The workshop's models by name (null: none of them). */
export const tallerModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'grillo':
      return grillo(o);
    case 'nadd':
      return nadd(o);
    case 'dunna':
      return dunna(o);
    case 'tormoHull':
      return tormoHull(o);
    case 'workGolem':
      return workGolem(o);
    case 'capataz':
      return capataz(o);
    default:
      return null;
  }
};
